/**
 * DineOps screen catalogue — every screen, its stable code, its module and the
 * cycle that builds it. One file, so that adding a screen is one entry here plus
 * one grant, and never a route string typed into a component.
 *
 * THE CODES ARE STABLE AND MUST NOT BE RENAMED.
 * The sixteen prefixed AUTH / TABLE / ORDER / KOT / KDS / BILL / SHIFT / DAY
 * codes come from `Restaurant Management Application.md` and are already cited
 * in its business rules (BR-035 names BILL-002 and BILL-003, for instance).
 * Renaming one silently breaks a rule that refers to it. The MENU, SETUP and
 * RPT codes are new here, because that document never assigned any.
 *
 * WHERE EACH SCREEN LIVES — three front ends, not one (plan §2.2, §5)
 *   'web'     a route in this app, inside the CRS shell
 *   'kiosk'   a route in this app with no shell: the kitchen display is a screen
 *             on a wall, and a sidebar on it is wasted pixels
 *   'waiter'  the separate PWA in dineops/dineops-waiter-app. Listed here so the
 *             catalogue is the whole surface, but it has NO route in this app —
 *             a waiter's device carries a waiter's app and none of the hotel.
 *
 * MODULE CODES must match crs.Core/Constants/DineOpsModule/DineOpsPermissions.cs
 * and the grants migration. Three places, one vocabulary.
 */

export type DineOpsSurface = 'web' | 'kiosk' | 'waiter';

export interface DineOpsScreen {
  /** Stable screen code. Never renamed — business rules cite these. */
  code: string;
  /** What the user sees in the menu and the page header. */
  label: string;
  /** Module code gating it, matching DineOpsPermissions.Modules. */
  module: string;
  /** Which front end it belongs to. */
  surface: DineOpsSurface;
  /** Route path, or null for a screen this app does not host. */
  path: string | null;
  /** Key in COMPONENT_REGISTRY, or null when not built yet. */
  component: string | null;
  /** The cycle that builds it, per the implementation plan §7. */
  cycle: number;
}

/**
 * Module codes, matching `admin.tbl_role_permissions.visible_modules` and
 * `crs.Core/Constants/DineOpsModule/DineOpsPermissions.cs`.
 *
 * A code IS the sidebar menu item id. `crs-web` filters a hardcoded
 * `navigationMenu` against `visibleModules` by matching item id to module code
 * (see login-interface.tsx), so a code that is not a menu id yields a screen
 * that never appears however it is granted. That is why these are kebab-case
 * and one per menu entry, exactly like CRS's own `property-master` and
 * `discount-report` — and not like the `MOB-*` codes, which belong to the
 * mobile app's separate catalogue.
 */
export const DINE_MODULES = {
  /** Retired from the sidebar 2026-10-08; its tabs became the four entries below. */
  SETUP: 'dineops-setup',
  DEPARTMENTS: 'dineops-departments',
  /** Kitchens and the stations set up in them, each kitchen linked to an org unit (2026-10-22). */
  KITCHENS: 'dineops-kitchens',
  TABLES: 'dineops-tables',
  SECTIONS: 'dineops-sections',
  QUICK_NOTES: 'dineops-quick-notes',
  OUTLETS: 'dineops-outlets',
  MENU: 'dineops-menu',
  /** DO-312: the dishes the header outlet sells; the menu itself is one master. */
  OUTLET_ITEMS: 'dineops-outlet-items',
  /** DO-313: cash counters; a bill goes to its section's counter. */
  BILLING_POINTS: 'dineops-billing-points',
  /** Approval rules (S3, 2026-10-29): which restaurant approvals are asked, of which role. Tenant-wide. */
  APPROVAL_RULES: 'dineops-approval-rules',
  ADDON_GROUPS: 'dineops-addon-groups',
  TAGS: 'dineops-tags',
  CHARGES: 'dineops-charges',
  SPECIALS: 'dineops-specials',
  PRICE_LISTS: 'dineops-price-lists',
  /** Price Board (item pricing P1, 2026-11-03): every dish's price in every outlet. Not bound to the header outlet. */
  PRICE_BOARD: 'dineops-price-board',
  ORDER: 'dineops-orders',
  KITCHEN: 'dineops-kds',
  CASHIER: 'dineops-bills',
  SHIFT: 'dineops-shifts',
  REVIEW: 'dineops-review',
  DAY_CLOSE: 'dineops-day-close',
  /** Buffet check-off board (buffet slice 2): waiter app only. */
  BUFFET: 'dineops-buffet',
  /** Department requisitions (R1, 2026-10-22): raise, send to the kitchen, issue; never billed. */
  REQUISITIONS: 'dineops-requisitions',
  REPORT_REQUISITIONS: 'dineops-requisition-report',
  /** Bill Register (S2, 2026-10-29): every bill of a period, the legacy POS register's columns. */
  REPORT_BILL_REGISTER: 'dineops-bill-register',
  REPORT_DASHBOARD: 'dineops-dashboard-report',
  REPORT_SALES: 'dineops-sales-report',
  REPORT_SETTLEMENT: 'dineops-settlement-report',
  REPORT_WASTAGE: 'dineops-wastage-report',
  REPORT_COMPLAINTS: 'dineops-complaints-report',
  REPORT_KITCHEN: 'dineops-kitchen-report',
} as const;

/** Every route this app hosts sits under one prefix. */
export const DINE_ROUTE_PREFIX = '/dineops';

export const DINEOPS_SCREENS: DineOpsScreen[] = [
  // ------------------------------------------------ menu master (cycle 1, web)
  {
    code: 'MENU-001',
    label: 'Menu Items',
    module: DINE_MODULES.MENU,
    surface: 'web',
    path: `${DINE_ROUTE_PREFIX}/menu`,
    component: 'DineOpsMenuItemsScreen',
    cycle: 1,
  },
  { code: 'MENU-009', label: 'Outlet Items', module: DINE_MODULES.OUTLET_ITEMS, surface: 'web',
    path: `${DINE_ROUTE_PREFIX}/outlet-items`, component: 'DineOpsOutletItemsScreen', cycle: 1 },
  { code: 'MENU-010', label: 'Price Board', module: DINE_MODULES.PRICE_BOARD, surface: 'web',
    path: `${DINE_ROUTE_PREFIX}/price-board`, component: 'DineOpsPriceBoardScreen', cycle: 13 },
  {
    code: 'MENU-002',
    label: 'Add-on Groups',
    module: DINE_MODULES.ADDON_GROUPS,
    surface: 'web',
    path: `${DINE_ROUTE_PREFIX}/addon-groups`,
    component: 'DineOpsAddonGroupsScreen',
    cycle: 1,
  },
  {
    code: 'MENU-006',
    label: 'Tags',
    module: DINE_MODULES.TAGS,
    surface: 'web',
    path: `${DINE_ROUTE_PREFIX}/tags`,
    component: 'DineOpsTagsScreen',
    cycle: 1,
  },
  {
    code: 'MENU-003',
    label: 'Charges',
    module: DINE_MODULES.CHARGES,
    surface: 'web',
    path: `${DINE_ROUTE_PREFIX}/charges`,
    component: null,
    cycle: 2,
  },
  {
    code: 'MENU-004',
    label: "Today's Special",
    module: DINE_MODULES.SPECIALS,
    surface: 'web',
    path: `${DINE_ROUTE_PREFIX}/specials`,
    component: 'DineOpsSpecialsScreen',
    cycle: 6,
  },
  {
    code: 'MENU-005',
    label: 'Price Lists',
    module: DINE_MODULES.PRICE_LISTS,
    surface: 'web',
    path: `${DINE_ROUTE_PREFIX}/price-lists`,
    component: 'DineOpsPriceListsScreen',
    cycle: 11,
  },

  // ------------------------------------------------ outlet setup (cycle 1, web)
  // SETUP-001 was one screen with four tabs; since 2026-10-08 each tab is its
  // own sidebar entry. The code is kept so nothing that cites it breaks.
  {
    code: 'SETUP-001',
    label: 'Outlet Setup',
    module: DINE_MODULES.SETUP,
    surface: 'web',
    path: `${DINE_ROUTE_PREFIX}/setup`,
    component: 'DineOpsOutletSetupScreen',
    cycle: 1,
  },
  { code: 'SETUP-009', label: 'Kitchens', module: DINE_MODULES.KITCHENS, surface: 'web',
    path: `${DINE_ROUTE_PREFIX}/kitchens`, component: 'DineOpsKitchensScreen', cycle: 1 },
  { code: 'SETUP-002', label: 'Kitchen Stations', module: DINE_MODULES.DEPARTMENTS, surface: 'web',
    path: `${DINE_ROUTE_PREFIX}/departments`, component: 'DineOpsDepartmentsScreen', cycle: 1 },
  { code: 'SETUP-003', label: 'Tables', module: DINE_MODULES.TABLES, surface: 'web',
    path: `${DINE_ROUTE_PREFIX}/tables`, component: 'DineOpsTablesScreen', cycle: 1 },
  { code: 'SETUP-004', label: 'Sections', module: DINE_MODULES.SECTIONS, surface: 'web',
    path: `${DINE_ROUTE_PREFIX}/sections`, component: 'DineOpsSectionsScreen', cycle: 1 },
  { code: 'SETUP-005', label: 'Quick Notes', module: DINE_MODULES.QUICK_NOTES, surface: 'web',
    path: `${DINE_ROUTE_PREFIX}/quick-notes`, component: 'DineOpsQuickNotesScreen', cycle: 1 },
  // Multi-outlet (plan O9, 2026-10-08): outlets. Who may work where is set on the
  // role (Role Permissions > Restaurant tab); SETUP-007 Outlet Access was retired 2026-10-09.
  { code: 'SETUP-006', label: 'Outlets', module: DINE_MODULES.OUTLETS, surface: 'web',
    path: `${DINE_ROUTE_PREFIX}/outlets`, component: 'DineOpsOutletsScreen', cycle: 1 },
  { code: 'SETUP-008', label: 'Billing Points', module: DINE_MODULES.BILLING_POINTS, surface: 'web',
    path: `${DINE_ROUTE_PREFIX}/billing-points`, component: 'DineOpsBillingPointsScreen', cycle: 4 },
  { code: 'SETUP-009', label: 'Approval Rules', module: DINE_MODULES.APPROVAL_RULES, surface: 'web',
    path: `${DINE_ROUTE_PREFIX}/approval-rules`, component: 'DineOpsApprovalRulesScreen', cycle: 12 },

  // ------------------------------------------------------ cashier (cycles 4–5)
  {
    code: 'BILL-003',
    label: 'Bill Queue',
    module: DINE_MODULES.CASHIER,
    surface: 'web',
    path: `${DINE_ROUTE_PREFIX}/bills`,
    component: 'DineOpsBillQueueScreen',
    cycle: 4,
  },
  {
    // BR-035: the detail view of ONE bill, opened from a row in BILL-003.
    // Deliberately no menu entry — one would have to guess which bill.
    code: 'BILL-002',
    label: 'Settlement',
    module: DINE_MODULES.CASHIER,
    surface: 'web',
    path: `${DINE_ROUTE_PREFIX}/bills/:billId`,
    component: null,
    cycle: 4,
  },
  {
    code: 'SHIFT-001',
    label: 'Shifts',
    module: DINE_MODULES.SHIFT,
    surface: 'web',
    path: `${DINE_ROUTE_PREFIX}/shifts`,
    component: 'DineOpsShiftsScreen',
    cycle: 5,
  },
  {
    code: 'REVIEW-001',
    label: 'Corrections Review',
    module: DINE_MODULES.REVIEW,
    surface: 'web',
    path: `${DINE_ROUTE_PREFIX}/review`,
    component: 'DineOpsCorrectionsScreen',
    cycle: 5,
  },
  { code: 'REQ-001', label: 'Requisitions', module: DINE_MODULES.REQUISITIONS, surface: 'web',
    path: `${DINE_ROUTE_PREFIX}/requisitions`, component: 'DineOpsRequisitionsScreen', cycle: 11 },
  {
    code: 'DAY-001',
    label: 'Day Close',
    module: DINE_MODULES.DAY_CLOSE,
    surface: 'web',
    path: `${DINE_ROUTE_PREFIX}/day-close`,
    component: 'DineOpsDayCloseScreen',
    cycle: 5,
  },

  // ------------------------------------------------------ reports (cycle 9)
  {
    code: 'RPT-001',
    label: 'Dashboard',
    module: DINE_MODULES.REPORT_DASHBOARD,
    surface: 'web',
    path: `${DINE_ROUTE_PREFIX}/reports/dashboard`,
    component: 'DineOpsDashboardScreen',
    cycle: 9,
  },
  {
    code: 'RPT-002',
    label: 'Sales',
    module: DINE_MODULES.REPORT_SALES,
    surface: 'web',
    path: `${DINE_ROUTE_PREFIX}/reports/sales`,
    component: 'DineOpsSalesReportScreen',
    cycle: 9,
  },
  {
    code: 'RPT-003',
    label: 'Settlement',
    module: DINE_MODULES.REPORT_SETTLEMENT,
    surface: 'web',
    path: `${DINE_ROUTE_PREFIX}/reports/settlement`,
    component: 'DineOpsSettlementReportScreen',
    cycle: 9,
  },
  {
    code: 'RPT-004',
    label: 'Wastage',
    module: DINE_MODULES.REPORT_WASTAGE,
    surface: 'web',
    path: `${DINE_ROUTE_PREFIX}/reports/wastage`,
    component: 'DineOpsWastageReportScreen',
    cycle: 9,
  },
  {
    code: 'RPT-005',
    label: 'Complaints',
    module: DINE_MODULES.REPORT_COMPLAINTS,
    surface: 'web',
    path: `${DINE_ROUTE_PREFIX}/reports/complaints`,
    component: 'DineOpsComplaintsReportScreen',
    cycle: 9,
  },
  {
    code: 'RPT-006',
    label: 'Kitchen Performance',
    module: DINE_MODULES.REPORT_KITCHEN,
    surface: 'web',
    path: `${DINE_ROUTE_PREFIX}/reports/kitchen`,
    component: 'DineOpsKitchenReportScreen',
    cycle: 9,
  },
  { code: 'RPT-007', label: 'Requisition Register', module: DINE_MODULES.REPORT_REQUISITIONS, surface: 'web',
    path: `${DINE_ROUTE_PREFIX}/reports/requisitions`, component: 'DineOpsRequisitionRegisterScreen', cycle: 11 },
  { code: 'RPT-008', label: 'Bill Register', module: DINE_MODULES.REPORT_BILL_REGISTER, surface: 'web',
    path: `${DINE_ROUTE_PREFIX}/reports/bill-register`, component: 'DineOpsBillRegisterScreen', cycle: 12 },

  // ----------------- kitchen display (cycle 3): a Restaurant tab, and /kitchen-display for the wall
  {
    code: 'KDS-001',
    label: 'Kitchen Display',
    module: DINE_MODULES.KITCHEN,
    surface: 'web',
    path: '/kitchen-display',
    component: 'DineOpsKitchenDisplayScreen',
    cycle: 3,
  },

  // ------------------------------------- the waiter app: no route in this app
  { code: 'AUTH-001',  label: 'Sign In',      module: DINE_MODULES.ORDER, surface: 'waiter', path: null, component: null, cycle: 0 },
  { code: 'TABLE-001', label: 'Table Board',  module: DINE_MODULES.ORDER, surface: 'waiter', path: null, component: null, cycle: 2 },
  { code: 'ORDER-001', label: 'Order Entry',  module: DINE_MODULES.ORDER, surface: 'waiter', path: null, component: null, cycle: 2 },
  { code: 'ORDER-005', label: 'Item Picker',  module: DINE_MODULES.ORDER, surface: 'waiter', path: null, component: null, cycle: 2 },
  { code: 'ORDER-003', label: 'Check',        module: DINE_MODULES.CASHIER, surface: 'waiter', path: null, component: null, cycle: 4 },
  { code: 'ORDER-004', label: 'Check Split',  module: DINE_MODULES.CASHIER, surface: 'waiter', path: null, component: null, cycle: 5 },
  { code: 'KOT-001',   label: 'Kitchen Tab',  module: DINE_MODULES.KITCHEN, surface: 'waiter', path: null, component: null, cycle: 3 },
  { code: 'BILL-001',  label: 'Bill Request', module: DINE_MODULES.CASHIER, surface: 'waiter', path: null, component: null, cycle: 4 },
  { code: 'BILL-004',  label: 'Room Lookup',  module: DINE_MODULES.CASHIER, surface: 'waiter', path: null, component: null, cycle: 7 },
  { code: 'BILL-005',  label: 'Split Assignment', module: DINE_MODULES.CASHIER, surface: 'waiter', path: null, component: null, cycle: 5 },
  { code: 'DASH-001',  label: 'Shift Figures', module: DINE_MODULES.SHIFT, surface: 'waiter', path: null, component: null, cycle: 5 },
  { code: 'BUF-001',   label: 'Buffet Check-off', module: DINE_MODULES.BUFFET, surface: 'waiter', path: null, component: null, cycle: 8 },
];

/** Screens this app actually routes — everything with a path and a component. */
export const dineOpsRoutableScreens = (): DineOpsScreen[] =>
  DINEOPS_SCREENS.filter((s) => s.path !== null && s.component !== null);

/** Screens that belong in the CRS sidebar: web surface, built, and not a detail view. */
export const dineOpsMenuScreens = (): DineOpsScreen[] =>
  dineOpsRoutableScreens().filter((s) => s.surface === 'web' && !s.path!.includes(':'));

/** Look a screen up by its stable code. */
export const dineOpsScreenByCode = (code: string): DineOpsScreen | undefined =>
  DINEOPS_SCREENS.find((s) => s.code === code);
